import scribe from '@grab-x/scribe-web-sdk';
import { fromPairs, flow, keys, map } from 'lodash/fp';

type Facets = Record<string, any>;

// When Scribe is first imported, it registers itself under the window object.
// We use mex portal's sidebar menu components, which are possible if not now
// but in the future that menu components has Sribe setup.
//
// Because we can setup common facets in Scribe, it'll be troublesome that our
// Scribe setup will be overwritten or misconfigured by mex portal setup.
//
// We workaround this by:
// 1. Guarding scribe usage w/ tracker, which don't use the window object
//    reference but imported referece.
// 2. Replace Scribe reference under window object to prevent misconfigured
//    by mex portal setup. This might cause mex portal menu scribe feature not
//    working as expected, which is acceptable for us.
window.scribesdk = {
  initialize: () => {},
  sendEvent: () => {},
  forceUpdate: () => {},
  setFacets: () => {},
};
type ArgumentTypes<F extends (...args: any[]) => any> = F extends (...args: infer A) => any ? A : never;

type AnyFunc = (...args: any[]) => any;

const withErrorHandling = <F extends AnyFunc>(func: F): ((...args: ArgumentTypes<F>) => ReturnType<F> | void) => {
  return (...args) => {
    try {
      const returned = func(...args);
      if (returned instanceof Promise) {
        return returned.catch(console.error);
      }
      return returned;
    } catch (err) {
      console.error(err);
      return undefined;
    }
  };
};

const COMMON_FACETS = new Set([
  'user_id',
  'subsession_id',
  'user_type',
  'mex_type',
  'merchant_grab_id',
  'store_grab_id',
  'master_merchant_grab_id',
  'entity_group_id',
  'grab_food_entity_id',
  'parent_entity_id',
  'troy_login_type',
  'troy_user_role',
  'country_code',
  'deeplink_source',
  'state',
]);

// addFacetPrefix adds prefix to non common facets
const addFacetPrefix = (facets: Facets): Facets =>
  flow([keys, map((name: string) => [COMMON_FACETS.has(name) ? name : `e_${name}`, facets[name]]), fromPairs])(facets);

const tracker = Object.freeze({
  initialize: withErrorHandling(scribe.initialize),
  sendEvent: withErrorHandling((eventName: string, facets: Facets, flush: boolean = true) => {
    const promises: Promise<any>[] = [];
    promises.push(scribe.sendEvent(eventName, addFacetPrefix(facets)));
    if (flush) promises.push(scribe.forceUpdate());
    return Promise.all(promises);
  }),
  forceUpdate: withErrorHandling(scribe.forceUpdate),
  setFacets: withErrorHandling(scribe.setFacets),
  setAuthToken: withErrorHandling(scribe.setAuthToken),
  // this is the original sendEvent.
  __sendEvent__: withErrorHandling(scribe.sendEvent),
});

export default tracker;

export type Tracker = typeof tracker;
